<template>
  <button
    :class="['base-button', variant, size, { 'full-width': fullWidth, 'disabled': disabled }]"
    :disabled="disabled"
    @click="handleClick"
  >
    <slot></slot>
  </button>
</template>

<script>
import { useHaptics } from '../../composables/useHaptics.js'

export default {
  name: 'BaseButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'text'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hapticType: {
      type: String,
      default: 'light',
      validator: (value) => ['light', 'medium', 'heavy', 'rigid', 'soft', 'success', 'error', 'warning'].includes(value)
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    const { light, medium, heavy, rigid, soft, success, error, warning } = useHaptics()

    const handleClick = (event) => {
      if (props.disabled) return

      // Вызываем соответствующую тактильную обратную связь
      switch (props.hapticType) {
        case 'medium':
          medium()
          break
        case 'heavy':
          heavy()
          break
        case 'rigid':
          rigid()
          break
        case 'soft':
          soft()
          break
        case 'success':
          success()
          break
        case 'error':
          error()
          break
        case 'warning':
          warning()
          break
        case 'light':
        default:
          light()
          break
      }

      emit('click', event)
    }

    return {
      handleClick
    }
  }
}
</script>

<style scoped>
.base-button {
  border: none;
  outline: none;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  border-radius: var(--radius-md);
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  background: var(--color-bg-card);
}

.base-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Размеры */
.base-button.sm {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  min-height: 36px;
}

.base-button.md {
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--font-size-base);
  min-height: 48px;
}

.base-button.lg {
  padding: var(--spacing-lg) var(--spacing-xl);
  font-size: var(--font-size-lg);
  min-height: 56px;
}

/* Варианты */
.base-button.primary {
  background: var(--color-accent);
  color: var(--color-bg-primary);
}

.base-button.primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.base-button.secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
}

.base-button.secondary:hover:not(:disabled) {
  background: var(--color-bg-secondary);
}

.base-button.outline {
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
}

.base-button.outline:hover:not(:disabled) {
  background: var(--color-bg-card);
}

.base-button.text {
  background: transparent;
  color: var(--color-text-primary);
}

.base-button.text:hover:not(:disabled) {
  opacity: 0.8;
}

.base-button.full-width {
  width: 100%;
}
</style>

